import React, { useState, useRef, useEffect } from 'react';

/**
 * Searchable dropdown component for hospices with parent-child hierarchy
 * Displays parent hospices with their child hospices nested underneath
 * 
 * @component
 * @param {Object} props
 * @param {Array} props.hospices - Flat array of hospice objects with id, name, and parentId
 * @param {string|Array} props.value - Currently selected value(s) - hospice id(s)
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Disable the select
 * @param {boolean} props.multiple - Enable multiple selection with checkboxes
 * @param {number} props.maxHeight - Maximum height for dropdown list (default: 300)
 * @param {boolean} props.required - Mark field as required
 */
export default function HospiceSearchableSelect({ 
  hospices = [], 
  value, 
  onChange, 
  placeholder = "Select hospice...",
  className = "",
  disabled = false,
  multiple = false,
  maxHeight = 300,
  required = false
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Normalize value to array for multiple mode
  const selectedValues = multiple 
    ? (Array.isArray(value) ? value : []) 
    : (value ? [value] : []);

  // Build hierarchy: group children under parents
  // Children whose parent is not in the list (e.g. already assigned) are promoted to top-level
  const buildHierarchy = (hospiceList) => {
    const parents = hospiceList.filter(h => !h.parentId);
    const children = hospiceList.filter(h => h.parentId);
    const parentIds = new Set(parents.map(p => p.id));

    const orphanedChildren = children.filter(c => !parentIds.has(c.parentId));
    const attachedChildren = children.filter(c => parentIds.has(c.parentId));

    const hierarchy = parents.map(parent => ({
      ...parent,
      children: attachedChildren.filter(child => child.parentId === parent.id)
    }));

    // Promote orphaned children as standalone top-level entries
    orphanedChildren.forEach(child => {
      hierarchy.push({ ...child, children: [] });
    });

    return hierarchy;
  };

  // Flatten hierarchy for filtering and display
  const flattenHierarchy = (hierarchy) => {
    const flattened = [];
    hierarchy.forEach(parent => {
      flattened.push({ ...parent, isParent: true, level: 0 });
      parent.children.forEach(child => {
        flattened.push({ ...child, isChild: true, level: 1 });
      });
    });
     console.log('flattened array: ', flattened);
    return flattened;
   
  };

  const hierarchy = buildHierarchy(hospices);
  const flatHospices = flattenHierarchy(hierarchy);

  // Filter based on search term - matches parent or child names
  const filteredHospices = searchTerm.trim()
    ? flatHospices.filter(h => {
        const matchesSearch = h.name.toLowerCase().includes(searchTerm.toLowerCase());
        
        // If parent matches, include parent and all children
        if (h.isParent && matchesSearch) {
          return true;
        }
        
        // If child matches, include it
        if (h.isChild && matchesSearch) {
          return true;
        }
        
        // Include parent if any of its children match
        if (h.isParent) {
          const parent = hierarchy.find(p => p.id === h.id);
          return parent?.children.some(child => 
            child.name.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
        
        return false;
      })
    : flatHospices;

  // Get display text for selected value (single mode only)
  const getDisplayText = () => {
    if (multiple) {
      return "";
    } else {
      const selectedHospice = hospices.find(h => h.id === value);
      return selectedHospice ? selectedHospice.name : "";
    }
  };

  const displayText = getDisplayText();

  // Get label for a selected value
  const getSelectedLabel = (hospiceId) => {
    const hospice = hospices.find(h => h.id === hospiceId);
    return hospice ? hospice.name : hospiceId;
  };

  // Recalculate dropdown position using fixed coords so it escapes modal overflow clipping
  useEffect(() => {
    if (isOpen && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropHeight = Math.min(maxHeight, 300);
      if (spaceBelow >= dropHeight || spaceBelow >= spaceAbove) {
        setDropdownStyle({ top: rect.bottom, left: rect.left, width: rect.width });
      } else {
        setDropdownStyle({ bottom: window.innerHeight - rect.top, left: rect.left, width: rect.width });
      }
    }
  }, [isOpen, maxHeight]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(prev => 
            prev < filteredHospices.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredHospices.length) {
          handleSelect(filteredHospices[highlightedIndex]);
        } else if (!isOpen) {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
        break;
      default:
        if (!isOpen && e.key.length === 1) {
          setIsOpen(true);
        }
        break;
    }
  };

  // Handle option selection
  const handleSelect = (hospice) => {
    if (multiple) {
      // Toggle selection in multiple mode
      const newValues = selectedValues.includes(hospice.id)
        ? selectedValues.filter(v => v !== hospice.id)
        : [...selectedValues, hospice.id];
      onChange(newValues);
      // Keep dropdown open in multiple mode
      setSearchTerm("");
      setHighlightedIndex(-1);
      inputRef.current?.focus();
    } else {
      // Single selection - close dropdown
      onChange(hospice.id);
      setIsOpen(false);
      setSearchTerm("");
      setHighlightedIndex(-1);
    }
  };

  // Handle input focus
  const handleFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setHighlightedIndex(-1);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  return (
    <div ref={wrapperRef} className={`position-relative ${className}`}>
      <div className="input-group">
        <input
          ref={inputRef}
          type="text"
          className={`form-control ${disabled ? 'disabled' : ''}`}
          placeholder={multiple ? placeholder : (displayText || placeholder)}
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          required={required && selectedValues.length === 0}
          autoComplete="off"
        />
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          style={{ borderLeft: 'none' }}
        >
          <span className={`${isOpen ? 'rotate-180' : ''}`}>▼</span>
        </button>
      </div>

      {/* Selected items display (multiple mode) */}
      {multiple && selectedValues.length > 0 && (
        <div className="mt-2 d-flex flex-wrap gap-1">
          {selectedValues.map(hospiceId => (
            <span
              key={hospiceId}
              className="badge bg-primary d-flex align-items-center gap-1"
              style={{ fontSize: '0.875rem' }}
            >
              {getSelectedLabel(hospiceId)}
              <button
                type="button"
                className="btn-close btn-close-white"
                style={{ fontSize: '0.5rem' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(selectedValues.filter(v => v !== hospiceId));
                }}
                disabled={disabled}
                aria-label="Remove"
              />
            </span>
          ))}
        </div>
      )}

      {/* Dropdown list - rendered with fixed position to escape modal overflow clipping */}
      {isOpen && !disabled && (
        <div
          className="border rounded bg-white shadow-sm"
          style={{
            position: 'fixed',
            top: dropdownStyle.top,
            bottom: dropdownStyle.bottom,
            left: dropdownStyle.left,
            width: dropdownStyle.width,
            maxHeight: `${maxHeight}px`,
            overflowY: 'auto',
            zIndex: 20000
          }}
        >
          {filteredHospices.length === 0 ? (
            <div className="px-3 py-2 text-muted">No hospices found</div>
          ) : (
            <ul className="list-unstyled mb-0">
              {filteredHospices.map((hospice, index) => {
                const isSelected = selectedValues.includes(hospice.id);
                const isHighlighted = index === highlightedIndex;

                return (
                  <li
                    key={`${hospice.id}-${hospice.level}`}
                    className={`px-3 py-2 cursor-pointer ${
                      isHighlighted ? 'bg-light' : ''
                    } ${isSelected ? 'bg-primary bg-opacity-10' : ''}`}
                    style={{
                      paddingLeft: `${(hospice.level * 1.5) + 1}rem`,
                      cursor: 'pointer',
                      fontWeight: hospice.isParent ? '600' : '400',
                      borderLeft: hospice.isChild ? '3px solid #dee2e6' : 'none',
                      marginLeft: hospice.isChild ? '1rem' : '0'
                    }}
                    onClick={() => handleSelect(hospice)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {multiple && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="form-check-input me-2"
                        style={{ pointerEvents: 'none' }}
                      />
                    )}
                    {hospice.isChild && (
                      <span className="text-muted me-1">└─</span>
                    )}
                    {hospice.name}
                    {hospice.isParent && hospice.children && hospice.children.length > 0 && (
                      <span className="text-muted ms-1">
                        ({hospice.children.length} site{hospice.children.length !== 1 ? 's' : ''})
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
